import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch, type Ref } from 'vue';
import { mascotSheet, mascotStill, MASCOT_ART, type MascotStateId } from '../data/mascot';

interface DisplayArt {
  src: string;
  sheet: boolean;
  alt: string;
  state: MascotStateId;
}
/** Decode a replacement before revealing it; never fetch animation for hidden or reduced-motion views. */
export function useMascotArtwork(
  stage: Ref<HTMLElement | null>,
  state: Readonly<Ref<MascotStateId>>,
  reducedMotion: Readonly<Ref<boolean>>,
) {
  const art = shallowRef<DisplayArt | null>(null);
  const visible = ref(false);
  const pageVisible = ref(document.visibilityState === 'visible');
  const mobile = window.matchMedia('(max-width: 520px)');
  const small = ref(mobile.matches);
  const paused = computed(() => !visible.value || !pageVisible.value || reducedMotion.value);
  let observer: IntersectionObserver | undefined;
  let request = 0;
  let disposed = false;
  let pendingImage: HTMLImageElement | undefined;
  const updateMobile = () => {
    small.value = mobile.matches;
  };
  const updatePage = () => {
    pageVisible.value = document.visibilityState === 'visible';
  };
  async function decode(src: string) {
    const image = new Image();
    pendingImage = image;
    image.decoding = 'async';
    image.src = src;
    await image.decode();
    return image;
  }
  watch(
    [state, reducedMotion, visible, pageVisible, small],
    async ([nextState]) => {
      const id = ++request;
      if (nextState === 'unknown') {
        art.value = null;
        return;
      }
      if (!visible.value || !pageVisible.value) return;
      const alt = MASCOT_ART[nextState].alt;
      try {
        if (art.value?.state !== nextState || (reducedMotion.value && art.value.sheet)) {
          const src = mascotStill(nextState);
          await decode(src);
          if (id !== request || disposed) return;
          art.value = { src, sheet: false, alt, state: nextState };
        }
        if (reducedMotion.value) return;
        const src = mascotSheet(nextState, small.value);
        if (art.value?.src === src) return;
        await decode(src);
        if (id !== request || disposed) return;
        art.value = { src, sheet: true, alt, state: nextState };
      } catch {
        // The neutral placeholder or previously decoded art remains available.
        if (id === request && art.value?.state !== nextState) art.value = null;
      }
    },
    { immediate: true },
  );
  onMounted(() => {
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          visible.value = entry.isIntersecting;
        },
        { rootMargin: '80px' },
      );
      if (stage.value) observer.observe(stage.value);
    } else visible.value = true;
    mobile.addEventListener('change', updateMobile);
    document.addEventListener('visibilitychange', updatePage);
  });
  onBeforeUnmount(() => {
    disposed = true;
    request++;
    observer?.disconnect();
    if (pendingImage && !pendingImage.complete) pendingImage.src = '';
    mobile.removeEventListener('change', updateMobile);
    document.removeEventListener('visibilitychange', updatePage);
  });
  return { art, paused };
}
